import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
    addDocumentNoteFailure,
    addDocumentNoteSuccess,
    loadAssignedDocumentsFailure,
    loadAssignedDocumentsSuccess,
    loadDocumentNotes,
    loadDocumentNotesFailure,
    loadDocumentNotesSuccess,
    loadPendingDocuments,
    loadPendingDocumentsFailure,
    loadPendingDocumentsSuccess,
    postDocumentNote
} from 'src/app/compliance/store/actions/document-batch-details.actions';
import {
    selectComplianceAssignedUploaderId,
    selectComplianceCandidateId,
    selectComplianceIsCandidateUpload,
    selectComplianceIsSignedContract
} from 'src/app/compliance/store/selectors/compliance.selectors';
import { PendingDocuments } from '../../models/posted-documents-models';
import { DocumentUploadToolClient } from '../../api/document-upload-tool-client.service';
import { DocumentUploadToolVaccineService } from '../../posted-documents/document-upload-tool-vaccine.service';
import { ComplianceCandidateDocumentDetailDto } from '../../api/models/compliance-candidate-document-detail-dto.model';

@Injectable()
export class DocumentBatchDetailsEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _store: Store,
        private readonly _documentUploadService: DocumentUploadToolClient,
        private readonly _documentUploadVaccineService: DocumentUploadToolVaccineService
    ) {}

    getBatchPendingDocuments$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadPendingDocuments),
            concatLatestFrom(() => [
                this._store.select(selectComplianceCandidateId),
                this._store.select(selectComplianceAssignedUploaderId),
                this._store.select(selectComplianceIsSignedContract),
                this._store.select(selectComplianceIsCandidateUpload)
            ]),
            switchMap(([action, candidateId, assignedUploaderId, isSignedContract, isCandidateUpload]) => {
                return this._documentUploadService
                    .getCandidateDocumentDetails(candidateId, assignedUploaderId, isSignedContract, isCandidateUpload)
                    .pipe(
                        map((pending: ComplianceCandidateDocumentDetailDto[]) => {
                            return pending.map(
                                (doc: ComplianceCandidateDocumentDetailDto) =>
                                    ({
                                        id: doc.id,
                                        documentId: doc.documentId,
                                        documentType: doc.documentType,
                                        fileName: doc.fileName,
                                        usedPageCount: doc.usedPageCount,
                                        pageCount: doc.pageCount,
                                        percentComplete: doc.percentComplete,
                                        facilityName: doc.facilityName,
                                        contractStageCode: doc.contractStageCode,
                                        postedByUser: doc.postedByUser,
                                        isCandidateUpload: doc.isCandidateUploaded,
                                        pageNumbers: doc.pageNumbers,
                                        unusedPageNumbers: doc.unusedPageNumbers,
                                        assignedDocuments: (doc.assignedDocuments || []).map((assigned: any) => ({
                                            ...assigned,
                                            candidateDocumentId: doc.id,
                                            originalDocumentId: doc.documentId,
                                            isCovidDoc: this._documentUploadVaccineService.isVaccineSelected(
                                                'covid19',
                                                assigned.documentType?.id
                                            ),
                                            isCovidExemption: this._documentUploadVaccineService.isVaccineSelected(
                                                'covid19Exemption',
                                                assigned.documentType?.id
                                            )
                                        })),
                                        candidateName: doc.candidateName
                                    }) as PendingDocuments
                            );
                        }),
                        map((batch: PendingDocuments[]) => loadPendingDocumentsSuccess({ pendingDocs: batch })),
                        catchError((result) => of(loadPendingDocumentsFailure({ error: result })))
                    );
            })
        );
    });

    getBatchAssignedDocuments$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadPendingDocumentsSuccess),
            switchMap((action) => {
                if (action.pendingDocs && action.pendingDocs.length > 0) {
                    const assignedDocs = [];
                    action.pendingDocs.forEach((pending) =>
                        pending.assignedDocuments.forEach((doc) => assignedDocs.push(doc))
                    );

                    return of(loadAssignedDocumentsSuccess({ assignedDocs: assignedDocs }));
                } else {
                    return of(loadAssignedDocumentsFailure({ error: 'Failed to retrieve pending docs' }));
                }
            })
        );
    });

    getBatchDocumentNotes$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadPendingDocuments, loadDocumentNotes),
            concatLatestFrom(() => [
                this._store.select(selectComplianceCandidateId),
                this._store.select(selectComplianceAssignedUploaderId),
                this._store.select(selectComplianceIsSignedContract),
                this._store.select(selectComplianceIsCandidateUpload)
            ]),
            switchMap(([action, candidateId, assignedUploaderId, isSignedContract, isCandidateUpload]) => {
                return this._documentUploadService
                    .getCandidateNotes(candidateId, assignedUploaderId, isSignedContract, isCandidateUpload)
                    .pipe(
                        map((result) => loadDocumentNotesSuccess({ notes: result })),
                        catchError((error) => of(loadDocumentNotesFailure({ error: error })))
                    );
            })
        );
    });

    addNoteToDocument$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(postDocumentNote),
            switchMap((action) => {
                return this._documentUploadService
                    .createNoteForCandidateDocuments({
                        candidateDocumentIds: action.candidateDocumentIds,
                        note: action.note
                    })
                    .pipe(
                        map((result) => addDocumentNoteSuccess({ result: result })),
                        catchError((result) => of(addDocumentNoteFailure({ error: result })))
                    );
            })
        );
    });

    loadDocumentNotes$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(addDocumentNoteSuccess),
                map(() => {
                    this._store.dispatch(loadDocumentNotes());
                })
            );
        },
        { dispatch: false }
    );
}
